// Вкладка «Лиги»: страна → турниры. Порядок стран — серверный; по умолчанию свёрнуты (одна страна — раскрыта).
// Архивные лиги не прячем — у них свой ярлык (решение владельца бэкенда).
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLeagues } from '../api/leagueQueries';
import { useConfig } from '../api/queries';
import type { LeagueItem, LeaguesScreen as LeaguesData } from '../api/leagueTypes';
import { t } from '../i18n/i18n';
import { paths } from '../nav';
import { EmptyStateView, QueryFallback } from '../ui/States';
import { MaskIcon } from '../ui/MenuCell';
import { TeamLogo } from '../ui/TeamLogo';
import globeIcon from '../assets/icons/globe.svg';
import { Placeholder } from './Placeholder';
import styles from './LeaguesScreen.module.css';

export function leagueSubtitle(league: LeagueItem): string {
  const status = league.status === 'archive' ? t('mc_archive_label')
    : league.status === 'between_seasons' ? t('mc_between_seasons') : null;
  return [league.gender_label, league.scope_label, league.season?.name, status].filter(Boolean).join(' • ');
}

// Раскрытое/свёрнутое человеком поверх умолчания: id страны → раскрыта. Живёт в модуле — переживает уход в лигу
// и «Назад» (вкладка монтируется заново), как toggled в MatchesScreen (HQ-1737).
const toggledCountries = new Map<string, boolean>();

/** Раскрыта ли страна: выбор человека, иначе умолчание (единственная страна — раскрыта). */
export function countryExpanded(id: string, single: boolean, toggled: ReadonlyMap<string, boolean> = toggledCountries): boolean {
  return toggled.get(id) ?? single;
}

/** Переключить страну: запоминается в модуле. */
export function toggleCountry(id: string, single: boolean, toggled: Map<string, boolean> = toggledCountries): void {
  toggled.set(id, !countryExpanded(id, single, toggled));
}

export function LeaguesScreen({ path }: { path: string }) {
  const query = useLeagues(path);
  const navigate = useNavigate();
  // поиск уже в серверной шапке вкладки (с 10.10) — своё поле не дублируем
  const headerSearch = useConfig().data?.navigation?.headers?.leagues?.items.some((i) => i.action?.type === 'search') ?? false;
  if (!query.data) return <QueryFallback query={query} />;
  const data = query.data;
  if (data.empty_state && !data.countries?.length) return <EmptyStateView state={data.empty_state} onReload={() => void query.refetch()} />;
  return (
    <div className={styles.page}>
      {!headerSearch && (
        <button className={styles.search} onClick={() => navigate(paths.search())}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
          {t('mc_search_hint')}
        </button>
      )}
      {data.blocks_order.map((key) => key === 'countries' ? <Countries key={key} data={data} /> : null)}
      {!data.blocks_order.includes('countries') && <Placeholder title={t('mc_no_data_yet')} />}
    </div>
  );
}

function Countries({ data }: { data: LeaguesData }) {
  const navigate = useNavigate();
  const [, rerender] = useState(0);
  const single = data.countries.length === 1;
  const toggle = (id: string) => { toggleCountry(id, single); rerender((n) => n + 1); };
  return (
    <>
      {data.countries.map((country) => {
        const id = String(country.id ?? 'no-country');
        const expanded = countryExpanded(id, single);
        return (
          <section key={id} className={styles.card}>
            <button className={styles.country} onClick={() => toggle(id)} aria-expanded={expanded}>
              {/* без картинки (World на проде) — глобус в круге, а не буквы «Wor» кружка TeamLogo (HQ-1994) */}
              {country.image ? <TeamLogo src={country.image} code={country.name} size={28} />
                : <span className={styles.globe}><MaskIcon src={globeIcon} size={20} /></span>}
              <span className={styles.countryName}>{country.name ?? ''}</span>
              <span className={styles.count}>{country.leagues.length}</span>
              <span className={`${styles.chevron} ${expanded ? styles.up : ''}`} aria-hidden="true">⌄</span>
            </button>
            {expanded && country.leagues.map((league) => (
              <button key={league.id} className={styles.league} onClick={() => navigate(paths.league(league.id))}>
                <TeamLogo src={league.image} code={league.code} size={32} />
                <span className={styles.leagueText}>
                  <span className={styles.leagueName}>{league.name}</span>
                  <span className={styles.subtitle}>{leagueSubtitle(league)}</span>
                </span>
                {(league.upcoming ?? 0) > 0 && <span className={styles.count}>{league.upcoming}</span>}
                <span className={styles.chevron} aria-hidden="true">›</span>
              </button>
            ))}
          </section>
        );
      })}
    </>
  );
}
